import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
    LayoutGrid,
    ShieldCheck,
    ListChecks,
    BookOpen,
    CalendarClock,
    FileOutput,
    Archive,
    AlertTriangle,
    Clock,
    ClipboardList,
    Plug,
    LifeBuoy,
    Settings as SettingsIcon,
    ArrowUpRight,
    Search,
    ChevronsUpDown,
    Circle,
    LogOut,
    Sparkles,
    GitBranch,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useTour } from "./GuidedTour";
import { getSopLibrary, getFindingsLibrary } from "../lib/api";

const NavItem = ({ to, icon: Icon, children, testid, badge, onClick }) => {
    const inner = (
        <>
            <Icon size={15} strokeWidth={1.75} />
            <span className="tracking-tight flex-1">{children}</span>
            {badge != null && (
                <span className="mono text-[10px] px-1.5 py-[1px] rounded-sm bg-neutral-100 text-neutral-600 border border-hair">
                    {badge}
                </span>
            )}
        </>
    );
    const cls =
        "group flex items-center gap-3 px-3 py-[7px] border-l-2 text-sm transition-colors duration-200";
    if (onClick) {
        return (
            <button
                onClick={onClick}
                data-testid={testid}
                className={`${cls} border-transparent text-neutral-500 hover:text-[#0A0A0A] w-full text-left`}
            >
                {inner}
            </button>
        );
    }
    return (
        <NavLink
            to={to}
            end
            data-testid={testid}
            className={({ isActive }) =>
                `${cls} ${
                    isActive
                        ? "border-[#C2410C] text-[#0A0A0A] bg-[#fafafa]"
                        : "border-transparent text-neutral-500 hover:text-[#0A0A0A]"
                }`
            }
        >
            {inner}
        </NavLink>
    );
};

const SectionLabel = ({ children, right }) => (
    <div className="px-5 pt-5 pb-2 flex items-center justify-between">
        <div className="overline">{children}</div>
        {right}
    </div>
);

const soon = () => toast.info("Coming in the next release");

export default function AppLayout({ children }) {
    const navigate = useNavigate();
    const { user, organization, logout } = useAuth();
    const { start: startTour } = useTour();
    const [sopCount, setSopCount] = useState(null);
    const [findingsCount, setFindingsCount] = useState(null);

    useEffect(() => {
        let cancelled = false;
        if (!user || user === false) return;
        getSopLibrary("SOP")
            .then((d) => {
                if (!cancelled) setSopCount(d?.totals?.unique_sops ?? null);
            })
            .catch(() => {
                /* silent — sidebar badge is best-effort */
            });
        getFindingsLibrary()
            .then((d) => {
                if (!cancelled) setFindingsCount(d?.totals?.total ?? null);
            })
            .catch(() => {
                /* silent */
            });
        return () => {
            cancelled = true;
        };
    }, [user]);

    const initials = (user?.name || user?.email || "??")
        .split(/[ @._-]/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0].toUpperCase())
        .join("");

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    return (
        <div className="min-h-screen bg-[#fafafa] text-[#0a0a0a] flex">
            {/* Sidebar */}
            <aside className="w-64 shrink-0 border-r border-hair bg-white flex flex-col sticky top-0 h-screen overflow-y-auto">
                {/* Brand */}
                <button
                    onClick={() => navigate("/")}
                    className="flex items-center gap-3 px-5 h-16 border-b border-hair text-left shrink-0"
                    data-testid="sidebar-logo"
                >
                    <img
                        src="/audri-logo.png"
                        alt="AudRI"
                        className="w-9 h-9 rounded-sm shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                        <div className="heading font-bold leading-none tracking-tight text-lg">
                            audri
                        </div>
                        <div
                            className="mono text-[8px] uppercase tracking-[0.12em] text-neutral-500 font-semibold mt-[6px] whitespace-nowrap"
                        >
                            Realtime audits · GxP
                        </div>
                    </div>
                </button>

                {/* Workspace switcher */}
                <button
                    onClick={soon}
                    className="mx-3 mt-3 flex items-center gap-2 px-3 py-2 border border-hair rounded-sm hover:bg-[#fafafa] text-left"
                    data-testid="workspace-switch"
                >
                    <div className="w-6 h-6 rounded-sm bg-[#0a0a0a] text-white flex items-center justify-center mono text-[10px] font-bold">
                        AP
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-[13px] heading font-medium leading-none tracking-tight truncate">
                            {organization?.name || "—"}
                        </div>
                        <div className="mono text-[10px] text-neutral-500 mt-1 truncate">
                            {(organization?.plan || "trial").toUpperCase()} · {user?.role}
                        </div>
                    </div>
                    <ChevronsUpDown size={12} className="text-neutral-400" />
                </button>

                {/* Search */}
                <button
                    onClick={soon}
                    data-testid="search-btn"
                    className="mx-3 mt-3 flex items-center gap-2 px-3 py-2 border border-hair rounded-sm text-left text-sm text-neutral-500 hover:border-[#0a0a0a] hover:text-[#0a0a0a]"
                >
                    <Search size={14} strokeWidth={1.75} />
                    <span className="flex-1">Search systems…</span>
                    <span className="mono text-[10px] px-1.5 py-[1px] rounded-sm bg-neutral-100 border border-hair">
                        ⌘K
                    </span>
                </button>

                {/* Primary workspace */}
                <SectionLabel>Workspace</SectionLabel>
                <nav className="flex flex-col gap-[2px] px-2">
                    <NavItem to="/dashboard" icon={LayoutGrid} testid="nav-dashboard">
                        Dashboard
                    </NavItem>
                    <NavItem
                        onClick={soon}
                        icon={ShieldCheck}
                        testid="nav-systems"
                        badge="15"
                    >
                        Systems
                    </NavItem>
                    <NavItem
                        to="/findings"
                        icon={ListChecks}
                        testid="nav-findings"
                        badge={findingsCount ?? ""}
                    >
                        Findings
                    </NavItem>
                    <NavItem
                        to="/link-graph"
                        icon={GitBranch}
                        testid="nav-link-graph"
                    >
                        Link Graph
                    </NavItem>
                    <NavItem
                        onClick={soon}
                        icon={Archive}
                        testid="nav-snapshots"
                    >
                        Snapshots
                    </NavItem>
                    <NavItem
                        onClick={soon}
                        icon={CalendarClock}
                        testid="nav-calendar"
                    >
                        Audit Calendar
                    </NavItem>
                    <NavItem
                        onClick={soon}
                        icon={FileOutput}
                        testid="nav-reports"
                    >
                        Reports
                    </NavItem>
                </nav>

                {/* Compliance insights */}
                <SectionLabel>Compliance</SectionLabel>
                <nav className="flex flex-col gap-[2px] px-2">
                    <NavItem
                        to="/sops"
                        icon={BookOpen}
                        testid="nav-sop-library"
                        badge={sopCount ?? ""}
                    >
                        SOP Library
                    </NavItem>
                    <NavItem
                        onClick={soon}
                        icon={ClipboardList}
                        testid="nav-capa"
                        badge="12"
                    >
                        CAPAs
                    </NavItem>
                    <NavItem
                        onClick={soon}
                        icon={AlertTriangle}
                        testid="nav-deviations"
                        badge="3"
                    >
                        Deviations
                    </NavItem>
                    <NavItem
                        onClick={soon}
                        icon={Clock}
                        testid="nav-change-control"
                    >
                        Change Controls
                    </NavItem>
                </nav>

                {/* Integrations */}
                <SectionLabel>
                    Integrations
                </SectionLabel>
                <div className="px-5 space-y-[6px] pb-2">
                    {[
                        ["Veeva Vault", true],
                        ["SharePoint", true],
                        ["TrackWise Digital", true],
                        ["Documentum", false],
                    ].map(([name, connected]) => (
                        <div
                            key={name}
                            className="flex items-center gap-2 text-[11px]"
                        >
                            <Circle
                                size={7}
                                className={
                                    connected
                                        ? "fill-[#064E3B] text-[#064E3B]"
                                        : "fill-neutral-300 text-neutral-300"
                                }
                            />
                            <span
                                className={
                                    connected
                                        ? "text-neutral-700"
                                        : "text-neutral-400"
                                }
                            >
                                {name}
                            </span>
                            <span className="ml-auto mono text-[9px] tracking-wider uppercase text-neutral-400">
                                {connected ? "Live" : "Off"}
                            </span>
                        </div>
                    ))}
                    <button
                        onClick={soon}
                        data-testid="add-integration"
                        className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#C2410C] hover:text-[#9A3412]"
                    >
                        <Plug size={11} /> Add integration
                    </button>
                </div>

                <div className="flex-1" />

                {/* Status / ops */}
                <div className="border-t border-hair px-5 py-3 text-[11px] flex items-center gap-2">
                    <span className="w-[6px] h-[6px] rounded-full bg-[#064E3B] animate-pulse" />
                    <span className="text-neutral-600">All systems operational</span>
                    <span className="ml-auto mono text-neutral-400">v1.0.2</span>
                </div>

                {/* Footer actions */}
                <div className="border-t border-hair px-2 py-2 space-y-[1px]">
                    <NavItem
                        onClick={() => startTour()}
                        icon={Sparkles}
                        testid="nav-take-tour"
                    >
                        Take the tour
                    </NavItem>
                    <NavItem to="/settings" icon={SettingsIcon} testid="nav-settings">
                        Settings
                    </NavItem>
                    <NavItem onClick={soon} icon={LifeBuoy} testid="nav-help">
                        Help & docs
                    </NavItem>
                </div>

                {/* Profile */}
                <div className="border-t border-hair p-3 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#C2410C] to-[#7c2d12] text-white flex items-center justify-center text-[13px] font-bold heading shrink-0">
                        {initials}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-[13px] heading font-medium leading-tight truncate">
                            {user?.name || user?.email}
                        </div>
                        <div className="mono text-[10px] text-neutral-500 mt-1 truncate">
                            {user?.role} · {organization?.name}
                        </div>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="text-neutral-400 hover:text-[#991B1B]"
                        title="Sign out"
                        data-testid="btn-logout"
                    >
                        <LogOut size={14} />
                    </button>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 min-w-0">
                {user?.role === "Observer" && (
                    <div
                        data-testid="demo-banner"
                        className="flex flex-wrap items-center gap-3 px-6 py-2 border-b border-hair bg-[#fff7ed] text-[12px]"
                    >
                        <span className="mono text-[10px] uppercase tracking-widest text-[#C2410C]">
                            Live demo · read-only
                        </span>
                        <span className="text-neutral-700 flex-1 min-w-0">
                            Uploads, audit runs, edits and e-signatures are
                            disabled in this sandbox. Click around freely.
                        </span>
                        <button
                            type="button"
                            onClick={() => startTour()}
                            data-testid="demo-replay-tour"
                            className="inline-flex items-center gap-1 border border-hair bg-white text-[11px] px-3 py-1 rounded-sm hover:bg-neutral-50"
                        >
                            <Sparkles size={11} /> Replay 60-sec tour
                        </button>
                        <a
                            href="https://calendly.com/keshav-glifiq/glif-demo"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 bg-[#0a0a0a] text-white text-[11px] px-3 py-1 rounded-sm hover:bg-[#262626]"
                            data-testid="demo-book-call"
                        >
                            Book a 30-min walkthrough →
                        </a>
                    </div>
                )}
                {children}
            </main>
        </div>
    );
}

export const PageHeader = ({ eyebrow, title, right, sub }) => (
    <div className="border-b border-hair bg-white">
        <div className="px-8 md:px-12 py-8 flex items-start justify-between gap-6 flex-wrap">
            <div>
                {eyebrow && <div className="overline mb-3">{eyebrow}</div>}
                <h1 className="heading text-3xl md:text-4xl font-medium tracking-tight">
                    {title}
                </h1>
                {sub && (
                    <p className="text-sm text-neutral-500 mt-2 max-w-2xl">{sub}</p>
                )}
            </div>
            {right}
        </div>
    </div>
);
